import React from "react";

const styles = {
  wrap: {
    padding: 0,
    borderRadius: "12px",
    boxShadow: "#000 0px 1px 4px, #000 0px 1px 1px",
    overflow: "hidden",
    background: "#fff",
    fontFamily: "inherit",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 16px",
    background: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
    color: "#fff",
    fontSize: "15px",
    fontWeight: 600,
  },
  closeBtn: {
    background: "rgba(255,255,255,0.2)",
    border: "none",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "16px",
    lineHeight: 1,
    transition: "background 0.2s",
  },
  body: {
    padding: "16px",
  },
  section: {
    marginBottom: "16px",
  },
  sectionLast: {
    marginBottom: 0,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "13px",
    fontWeight: 600,
    marginBottom: "8px",
    color: "#333",
  },
  successTitle: {
    color: "#2e7d32",
  },
  errorTitle: {
    color: "#c62828",
  },
  icon: {
    width: "18px",
    height: "18px",
    flexShrink: 0,
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    fontSize: "13px",
    lineHeight: 1.6,
    color: "#555",
  },
  listItem: {
    marginBottom: "4px",
  },
  listItemLast: {
    marginBottom: 0,
  },
  errorItem: {
    color: "#555",
    wordBreak: "break-word",
  },
  errorReason: {
    color: "#c62828",
    fontWeight: 500,
  },
};

/**
 * Custom toast content for add-teachers email result.
 * Shows "Added successfully" only when addedEmails has items; shows "Not added" only when failedEmails has items.
 * @param {{ closeToast?: () => void, addedEmails?: string[], failedEmails?: { email: string, reason: string }[] }} props
 */
const HEADER_BG = {
  allSuccess: "#2e7d32",
  partial: "#FF9800",
  allFailed: "#c62828",
  default: "linear-gradient(135deg, #1a237e 0%, #283593 100%)",
};

export default function EmailResultToast({ closeToast, addedEmails = [], failedEmails = [] }) {
  const showAdded = addedEmails.length > 0;
  const showFailed = failedEmails.length > 0;
  const defaultAdded = showAdded ? addedEmails : [''];
  const defaultFailed = showFailed
    ? failedEmails
    : [''];
  const useDemo = !showAdded && !showFailed;

  const headerBackground =
    showAdded && !showFailed
      ? HEADER_BG.allSuccess
      : showAdded && showFailed
        ? HEADER_BG.partial
        : showFailed
          ? HEADER_BG.allFailed
          : HEADER_BG.default;

  return (
    <div style={styles.wrap} onClick={(e) => e.stopPropagation()}>
      <div style={{ ...styles.header, background: headerBackground }}>
        <span>Email results</span>
        <button
          type="button"
          style={styles.closeBtn}
          onClick={closeToast}
          onKeyDown={(e) => e.key === "Enter" && closeToast?.()}
          aria-label="Close"
        >
          ×
        </button>
      </div>
      <div style={styles.body}>
        {(showAdded || useDemo) && (
          <div style={styles.section}>
            <div style={{ ...styles.sectionTitle, ...styles.successTitle }}>
              <CheckIcon style={styles.icon} />
              Added successfully
            </div>
            <ul style={styles.list}>
              {(showAdded ? addedEmails : defaultAdded).map((email, i) => (
                <li
                  key={typeof email === "string" ? email : email.email || i}
                  style={{ ...styles.listItem, ...(i === (showAdded ? addedEmails : defaultAdded).length - 1 ? styles.listItemLast : {}) }}
                >
                  {typeof email === "string" ? email : email.email}
                </li>
              ))}
            </ul>
          </div>
        )}
        {(showFailed || useDemo) && (
          <div style={{ ...styles.section, ...styles.sectionLast }}>
            <div style={{ ...styles.sectionTitle, ...styles.errorTitle }}>
              <AlertIcon style={styles.icon} />
              Not added
            </div>
            <ul style={styles.list}>
              {(showFailed ? failedEmails : defaultFailed).map((item, i) => {
                const email = typeof item === "string" ? item : item.email;
                const reason = typeof item === "string" ? "" : item.reason;
                return (
                  <li
                    key={email}
                    style={{ ...styles.listItem, ...styles.errorItem, ...(i === (showFailed ? failedEmails : defaultFailed).length - 1 ? styles.listItemLast : {}) }}
                  >
                    {email}
                    {reason && (
                      <>
                        {" "}
                        <span style={styles.errorReason}>— {reason}</span>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function CheckIcon({ style }) {
  return (
    <svg style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function AlertIcon({ style }) {
  return (
    <svg style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}
